import "./index.less";
import * as React from "react";
import { AllSizes } from "@atman/design-system";
import { CheckMarkOption } from "./components/CheckMarkOption";
import { Colors } from "../../styles";
import { FieldError } from "@atman/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormGroup, FormText, Label } from "reactstrap";
import { FormatOptionLabelMeta } from "react-select/base";
import { IconMultiValueRemove } from "./components/IconMultiValueRemove";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { MaxItemsValueContainer } from "./components/MaxItemsValueContainer";
import { MutableRefObject } from "react";
import { SelectComponentsConfig } from "react-select/src/components";
import { Props as SelectProps } from "react-select/src/Select";
import { StylesConfig, StylesConfigFunction } from "react-select/src/styles";
import { ValueType } from "react-select/src/types";
import { inject, observer } from "mobx-react";
import BadgeMultiValueContainer from "./components/BadgeMultiValueContainer";
import Select, {
    ControlProps,
    FocusEventHandler,
    GroupType,
    GroupedOptionsType,
    components as ReactSelectComponents,
} from "react-select";

export type AcceptedValueType<TValue> = TValue | TValue[] | undefined;

export interface IAtAutoCompleteSelectFieldProps<TValue> {
    fieldName?: string;
    labelKey?: string;
    label?: string;
    options: TValue[] | GroupedOptionsType<TValue>;
    value: AcceptedValueType<TValue>;
    // @ts-ignore
    onChange: (option: ValueType<AcceptedValueType<TValue>>, fieldName?: string) => void;
    onBlur?: FocusEventHandler;
    onFocus?: FocusEventHandler;
    placeholder?: string;
    formText?: string;
    isMulti?: boolean;
    hideSelectedOptions?: boolean;
    closeMenuOnSelect?: boolean;

    formatOptionLabel?: (data: TValue, context: FormatOptionLabelMeta<TValue>) => React.ReactNode;
    formatGroupLabel?: (group: GroupType<TValue>) => React.ReactNode;
    icon?: IconProp;
    hideLabel?: boolean;
    isClearable?: boolean;
    isDisabled?: boolean;
    isSearchable?: boolean;
    additionalStyles?: StylesConfig;
    size?: AllSizes;

    errors?: FieldError[];

    innerRef?: ((instance: Select | null) => void) | MutableRefObject<Select | null> | null;

    maxNumberOfValuesToDisplay?: number;
}

@inject()
@observer
export class AtAutoCompleteSelectField<TValue> extends React.Component<IAtAutoCompleteSelectFieldProps<TValue>, {}> {
    onChangeHandler = (option) => {
        const { onChange, fieldName } = this.props;

        return onChange(option, fieldName);
    };

    render() {
        const {
            labelKey,
            options,
            value,
            placeholder,
            formText,
            formatOptionLabel,
            formatGroupLabel,
            icon,
            errors,
            isMulti = false,
            hideSelectedOptions = false,
            closeMenuOnSelect = true,
            hideLabel = false,
            isClearable = false,
            isSearchable = true,
            additionalStyles = { container: (styles) => ({}), control: (styles) => ({}) } as StylesConfig,
            isDisabled = false,
            innerRef,
            onBlur,
            onFocus,
            maxNumberOfValuesToDisplay,
            label,
            fieldName,
            size = "lg",
        } = this.props;

        const baseClassName = `AtAutoCompleteSelectField size-${size}`;

        const ValueContainer = (props) => {
            return (
                <ReactSelectComponents.ValueContainer {...props}>
                    {icon ? <FontAwesomeIcon icon={icon} style={{ marginRight: 12 }} /> : null}
                    {props.children}
                </ReactSelectComponents.ValueContainer>
            );
        };

        const errorStyles: StylesConfig = {
            control: (styles) => ({
                ...styles,
                borderColor: Colors.errorColor,
            }),
        };

        let sizingStyles: StylesConfigFunction<ControlProps<{}>> = (base) => base;
        if (size === "sm") {
            // This is a beginning. Add the other size as needed.
            sizingStyles = (base) => ({ ...base, minHeight: 32, height: 32, alignItems: "baseline" });
        }

        const errorsForField = errors?.filter((x) => x.fieldName === fieldName) ?? [];

        const defaultStyles: StylesConfig = icon ? reactSelectIconStyles : reactSelectStyles;
        const customStyles: StylesConfig = {
            ...defaultStyles,
            ...additionalStyles,
            container: (styles, state) => ({
                ...styles,
                ...(defaultStyles.container ? defaultStyles.container(styles, state) : {}),
                ...(additionalStyles.container ? additionalStyles.container(styles, state) : {}),
            }),
            control: (styles, state) => ({
                ...styles,
                ...(defaultStyles.control ? defaultStyles.control(styles, state) : {}),
                ...(additionalStyles.control ? additionalStyles.control(styles, state) : {}),
                ...(errorsForField.any() && errorStyles.control ? errorStyles.control(styles, state) : {}),
                ...sizingStyles(styles, state),
            }),
            // indicatorsContainer: (styles, state) => ({
            //     ...styles,
            //     ...defaultStyles?.indicatorsContainer ? defaultStyles.indicatorsContainer(styles, state) : {},
            //     ...additionalStyles.indicatorsContainer ? additionalStyles.indicatorsContainer(styles, state) : {}
            // })
        };

        let selectComponents: SelectComponentsConfig<TValue> = {};

        if (icon) {
            selectComponents = {
                ...selectComponents,
                ValueContainer,
            };
        }

        if (isMulti && maxNumberOfValuesToDisplay) {
            selectComponents = {
                ValueContainer: (props) => (
                    // @ts-ignore
                    <MaxItemsValueContainer
                        {...props}
                        icon={icon}
                        maxNumberOfValuesToDisplay={maxNumberOfValuesToDisplay}
                    />
                ),
                // @ts-ignore
                Option: CheckMarkOption,
                MultiValueContainer: BadgeMultiValueContainer,
                MultiValueRemove: IconMultiValueRemove,
            };
        }

        return (
            <FormGroup className={baseClassName}>
                {!hideLabel && (label || labelKey) ? <Label>{label ?? `global.${labelKey}`.localize()}</Label> : null}
                <form autoComplete={"none"}>
                    <Select
                        classNamePrefix="AtAutoCompleteSelectField"
                        components={selectComponents}
                        options={options}
                        value={value}
                        onChange={this.onChangeHandler}
                        styles={customStyles}
                        placeholder={placeholder ?? `${"global.select".localize()}...`}
                        isMulti={isMulti}
                        closeMenuOnSelect={closeMenuOnSelect}
                        hideSelectedOptions={isMulti ? false : hideSelectedOptions}
                        formatGroupLabel={formatGroupLabel}
                        formatOptionLabel={formatOptionLabel}
                        isClearable={isClearable}
                        isDisabled={isDisabled}
                        isSearchable={isSearchable}
                        noOptionsMessage={({ inputValue }) => "global.noOptions".localize()}
                        loadingMessage={({ inputValue }) => "global.loading".localize()}
                        ref={innerRef}
                        onBlur={onBlur}
                        onFocus={onFocus}
                    />
                </form>
                {errorsForField?.any()
                    ? errorsForField.map((x, i) => (
                          <FormText key={i} color={"danger"}>
                              {x.message}
                          </FormText>
                      ))
                    : formText && <FormText>{formText}</FormText>}
            </FormGroup>
        );
    }
}

export const reactSelectStyles: StylesConfig = {
    container: (styles, {}: IReactSelectStylingState) => ({
        ...styles,
    }),
    // @ts-ignore
    control: (styles, { menuIsOpen, isFocused }: IReactSelectStylingState) => ({
        ...styles,
        padding: "4px 8px",
        fontFamily: "Quicksand",
        fontSize: "15px",
        lineHeight: "20px",
        fontWeight: 500,
        borderRadius: 4,
        boxShadow: menuIsOpen ? Colors.inputBoxShadow : "none",
        borderColor: menuIsOpen
            ? Colors.inputBorderColor__Active
            : isFocused
            ? Colors.inputBorderColor__Hover
            : Colors.inputBorderColor,
        "&:hover": {
            borderColor: menuIsOpen ? Colors.inputBorderColor__Active : Colors.inputBorderColor__Hover,
        },
    }),
    valueContainer: (base, {}: IReactSelectStylingState) => ({
        ...base,
        padding: "0 8px",
    }),
    indicatorSeparator: (styles, {}: IReactSelectStylingState) => ({
        ...styles,
        display: "none",
    }),
    option: (styles, { isSelected, isFocused, isDisabled }: IReactSelectStylingState) => ({
        ...styles,
        fontWeight: isDisabled ? 400 : 500,
        pointerEvents: isDisabled ? "none" : undefined,
        backgroundColor: isFocused ? Colors.greyShades.shade9 : isSelected ? "white" : styles.backgroundColor,
        color: isDisabled ? Colors.greyShades.shade7 : isSelected ? Colors.primaryBase : Colors.greyBase,
        "&:hover": {
            backgroundColor: isSelected ? Colors.primaryShades.shade9 : Colors.greyShades.shade9,
            color: isSelected ? Colors.primaryBase : Colors.greyBase,
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    }),
    multiValueRemove: (base, {}) => ({
        ...base,
        "&:hover": {
            backgroundColor: "transparent",
            color: Colors.primaryBase,
        },
    }),
};

export const reactSelectIconStyles: StylesConfig = {
    ...reactSelectStyles,
    placeholder: (styles) => ({
        ...styles,
        paddingLeft: 30,
        color: Colors.greyShades.shade2,
    }),
    singleValue: (styles) => ({
        ...styles,
        paddingLeft: 30,
    }),
};

export interface IReactSelectStylingState {
    hasValue: boolean;
    isMulti: boolean;
    isRtl: boolean;
    isDisabled: boolean;
    isFocused: boolean;
    isSelected: boolean;
    menuIsOpen: boolean;
    selectProps: SelectProps<any>;
}
